import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CASE_STUDIES } from '../data/mock';

// Scroll to Top Button
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.scrollY > 400);
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
      aria-label="Scroll to top"
    >
      <ChevronUp size={20} />
    </button>
  );
};

const WorkPage = () => {
  const [activeTab, setActiveTab] = useState(CASE_STUDIES[0].id);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <main className="pt-24">
      {/* Hero Section */}
      <section className="section-padding section-champagne relative">
        <div className="absolute top-20 bottom-20 left-8 md:left-16 w-px bg-gradient-to-b from-transparent via-velora-gold/20 to-transparent" />

        <div className="container-luxury">
          <div className="max-w-4xl ml-8 md:ml-12">
            <span 
              className="label-sm mb-8 block animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.2s', animationFillMode: 'forwards', color: 'var(--velora-gold)' }}
            >
              Selected Work
            </span>
            <h1 
              className="display-hero mb-10 animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.4s', animationFillMode: 'forwards', color: 'var(--velora-green-deep)' }}
            >
              Case Studies
            </h1>
            <div 
              className="divider-gold mb-10 animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }} 
            />
            <p 
              className="body-editorial text-lg animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
            >
              A selection of strategic work that shaped perception, built authority, and
              delivered measured growth across industries and markets.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Tabs Section */}
      <section ref={sectionRef} className="section-padding section-ivory">
        <div className="container-luxury">
          <Tabs defaultValue={CASE_STUDIES[0].id} className="w-full">
            <TabsList 
              className={`w-full bg-transparent h-auto p-0 mb-20 flex flex-wrap justify-start gap-8 md:gap-12 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {CASE_STUDIES.map((study) => (
                <TabsTrigger
                  key={study.id}
                  value={study.id}
                  onClick={() => setActiveTab(study.id)}
                  className={`relative pb-4 px-0 bg-transparent rounded-none border-0 shadow-none text-left transition-all duration-500
                    data-[state=active]:shadow-none data-[state=active]:bg-transparent
                    after:absolute after:bottom-0 after:left-0 after:w-0 after:h-px after:bg-velora-gold after:transition-all after:duration-500
                    data-[state=active]:after:w-full
                  `}
                >
                  <span
                    className={`heading-md transition-all duration-500 ${
                      activeTab === study.id ? '' : 'opacity-35 hover:opacity-60'
                    }`}
                    style={{
                      color: activeTab === study.id ? 'var(--velora-green-deep)' : 'var(--velora-charcoal-light)',
                    }}
                  >
                    {study.title}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            {CASE_STUDIES.map((study) => (
              <TabsContent key={study.id} value={study.id} className="mt-0">
                <div 
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: '300ms' }}
                >
                  <div>
                    <span className="label-sm mb-8 block" style={{ color: 'var(--velora-gold)' }}>
                      {study.category}
                    </span>
                    <h2 className="display-md mb-10">{study.title}</h2>
                    <div className="divider-gold mb-10" />
                    <p className="body-editorial text-lg mb-12">{study.description}</p>
                    <div className="space-y-10">
                      <div>
                        <h4 className="label-sm mb-4" style={{ color: 'var(--velora-gold-muted)' }}>Challenge</h4>
                        <p className="body-md">{study.challenge}</p>
                      </div>
                      <div>
                        <h4 className="label-sm mb-4" style={{ color: 'var(--velora-gold-muted)' }}>Approach</h4>
                        <p className="body-md">{study.approach}</p>
                      </div>
                      <div>
                        <h4 className="label-sm mb-4" style={{ color: 'var(--velora-gold-muted)' }}>Outcome</h4>
                        <p className="body-md">{study.outcome}</p>
                      </div>
                      {/* Client Names - Subtle Editorial */}
                      <div className="pt-4">
                        <p 
                          className="text-sm leading-relaxed"
                          style={{ 
                            color: 'var(--velora-charcoal-light)', 
                            fontFamily: 'Inter, sans-serif',
                            opacity: 0.6
                          }}
                        >
                          {study.clients.join('  •  ')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <div 
                      className="aspect-[4/3] flex items-center justify-center relative overflow-hidden"
                      style={{ background: 'linear-gradient(145deg, var(--velora-green-deep) 0%, var(--velora-green) 100%)' }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-velora-gold/[0.04] to-transparent" />
                      <div className="text-center p-8 relative z-10">
                        <span className="label-sm block mb-4" style={{ color: 'var(--velora-gold)' }}>Confidential</span>
                        <p className="font-display text-lg" style={{ color: 'var(--velora-champagne)' }}>
                          Work samples available upon request
                        </p>
                      </div>
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-full h-full border border-velora-gold/20 -z-10" />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-green text-center relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-b from-velora-gold/[0.04] to-transparent rounded-full blur-3xl" />
        </div>
        <div className="container-luxury max-w-3xl relative z-10">
          <span className="label-sm mb-8 block" style={{ color: 'var(--velora-gold)' }}>Work With Us</span>
          <h2 className="display-md mb-10" style={{ color: 'var(--velora-ivory)' }}>
            Ready to shape your brand's future?
          </h2>
          <p className="body-editorial text-lg mb-14" style={{ color: 'var(--velora-champagne)' }}>
            We work with select brands and institutions that value strategic positioning over tactical execution.
          </p>
          <a
            href="https://wa.me/918208377252?text=Hello%20Ashwini%2C%0AI%20came%20across%20Velora%20and%20would%20like%20to%20request%20access%20to%20explore%20a%20potential%20collaboration."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-3.5 font-medium tracking-wider uppercase text-xs transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(196,169,98,0.35)]"
            style={{
              background: 'var(--velora-gold)',
              color: 'var(--velora-green-deep)',
            }}
            onMouseEnter={(e) => e.target.style.background = 'var(--velora-gold-light)'}
            onMouseLeave={(e) => e.target.style.background = 'var(--velora-gold)'}
          >
            Request Access
            <ArrowRight size={14} className="ml-3" />
          </a>
        </div>
      </section>

      <ScrollToTop />
    </main>
  );
};

export default WorkPage;
